/**
 * Renders the main sign-in screen and the related handling.
 *
 * Currently the following login methods are supported:
 *  - Login with Google
 *  - Login with username/password
 *
 * In addition, a link to the Moodle course is shown (if avaialble in the local storage)
 */

import React, {useContext, useEffect, useState} from "react";
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import {NavLink, Redirect} from 'react-router-dom';

import {FirebaseAuthContext} from 'components/commons/firebase/FirebaseAuthProvider';
import {firebaseService} from 'components/commons/firebase/Firebase';
import SignInButton from "components/commons/elements/buttons/SignInButton";
import useSocialSignIn from 'components/commons/signin/manualSignIn/useSocialSignIn';

import {useStyles} from "./SignInSectionMain.css";
import AppButton from "../../elements/buttons/AppButton";
import {useIntl} from "react-intl";
import {LinearProgress} from "@mui/material";
import logo from 'assets/judged_black_bg.png';

export default function SignInSectionMain() {
    const classes = useStyles()

    const {signedInUser} = useContext(FirebaseAuthContext)

    // Custom hook for the callback handler if the user signs-in via clicking on the Google button
    const {signInWithSocialProvider, signInWithEmailAndPassword, manualAuthInProgress} = useSocialSignIn()

    const [authEmail, setAuthEmail] = useState('');
    const [authPassword, setAuthPassword] = useState('');
    const [saveButtonEnabled, setSaveButtonEnabled] = useState(false);

    // Internationalization for simple strings
    const intl = useIntl();

    useEffect(() => {
        setSaveButtonEnabled(authEmail && authPassword)
    }, [authEmail, authPassword])

    const handleChangeEmail = (event) => {
        setAuthEmail(event.target.value)
    }

    const handleChangePassword = (event) => {
        setAuthPassword(event.target.value)
    }

    // Special handling, because we want that the user can press "ENTER" to trigger the login as well
    const handleSubmit = (e) => {
        e.preventDefault()
        signInWithEmailAndPassword(authEmail, authPassword)
    }

    /*
    If the user is already authenticated, a redirect to the app authorization entry point.
    This entry point forwards the user based on the respective authentication state and user roles.
    In such a case, no login form is shown and the user is immediately forwarded to the landing page.
     */
    if (signedInUser && !manualAuthInProgress) {
        return <Redirect to={"/authorizedGate"}/>;
    }

    return (
        <div>
            {manualAuthInProgress && <LinearProgress/>}
            <Container component="main" maxWidth="sm">
                <div className={classes.header}>
                    <div>
                        <img className={classes.logo} src={logo} alt='App logo'/>
                    </div>
                    <Typography component="h1" variant="h5">
                        {intl.formatMessage({
                            id: 'app.signIn.form.header',
                            defaultMessage: 'Sign in'
                        })}
                    </Typography>
                </div>

                <form className={classes.signInSection} onSubmit={handleSubmit}>
                    {/* Sign-in with username and password */}

                    {/* Email */}
                    <TextField variant="outlined" margin="normal" fullWidth required value={authEmail}
                               onChange={handleChangeEmail}
                               id="reg-user-email"
                               label={intl.formatMessage({
                                   id: 'app.signIn.form.username.label',
                                   defaultMessage: 'Email'
                               })}/>

                    {/* Password */}
                    <TextField variant="outlined" margin="normal" fullWidth required type="password"
                               value={authPassword}
                               onChange={handleChangePassword}
                               id="reg-user-password"
                               label={intl.formatMessage({
                                   id: 'app.signIn.form.password.label',
                                   defaultMessage: 'Password'
                               })}/>

                    <AppButton
                        label={intl.formatMessage({
                            id: 'app.signIn.form.button',
                            defaultMessage: 'Sign In'
                        })}
                        disabled={!saveButtonEnabled}/>
                </form>

                <div className={classes.signInLink}>
                    <Typography>
                            <span>{intl.formatMessage({
                                id: 'app.signIn.createAccount.question',
                                defaultMessage: 'No user yet?'
                            })} </span>
                        <NavLink to="/signUp">
                            {intl.formatMessage({
                                id: 'app.signIn.createAccount.link',
                                defaultMessage: 'Create an account.'
                            })}
                        </NavLink>
                    </Typography>
                </div>

                <div className={classes.oidcSection}>
                    {/* Sign-in with OIDC auth providers */}
                    <SignInButton provider='google' onClick={() => {
                        signInWithSocialProvider(new firebaseService.auth.GoogleAuthProvider())
                    }}/>
                    {
                        /*
                        Only show the login possibility in case the 'moodleReturnUrl' local storage is set.
                        If set, it always leads to the Moodle course, from which the app has been been most recently accessed.
                         */
                        !!localStorage.getItem('moodleReturnUrl') &&
                        <SignInButton provider='moodle' href={localStorage.getItem('moodleReturnUrl')}/>
                    }
                </div>
            </Container>
        </div>
    );
}
